// ENTRADA DE NAVEGADOR: uma tag `<script>` e a personalização funciona.
//
//     <script src="https://cdn.synqi3.ai/sdk/v2/synqi3.min.js"
//             data-synqi3-tenant="acme"
//             data-synqi3-token="sdk_..."
//             data-synqi3-api="https://api.synqi3.ai"
//             data-synqi3-user="cliente_42"></script>
//
//     <h1 data-synqi3-slot="hero">o texto que já está lá</h1>
//
// Este arquivo é a COLA da tag: lê a configuração dos atributos, monta o `Tracker` e roda o ciclo.
// Nenhuma regra de negócio mora aqui — ela está no `decisao.js`, no `resolve.js` e no `tracker.js`,
// que rodam igual em Node, Deno e edge. Trocar de superfície é trocar esta cola.
//
// ## Onde pôr a tag, e por quê
//
// No `<head>`. Medido em 21-22/08/2026: a janela entre o script poder rodar e a página pintar é de
// 86 ms, e uma ida até a região custa ~590 ms — sete vezes. A decisão guardada é aplicada em
// ~0,4 ms, e no teste real entrou no DOM 52,7 ms ANTES da primeira pintura. Tag no fim do `<body>`
// ainda funciona, mas encurta essa margem.
//
// ## O que ele NÃO faz
//
// Não inventa tenant, token nem usuário. Sem `data-synqi3-tenant` e `data-synqi3-token` não há o
// que fazer, e o SDK fica quieto em vez de sair pela rede com um palpite. Sem `data-synqi3-user`,
// o `Tracker` usa o anônimo que ele já mantém — que é o certo: a maioria dos visitantes de um site
// público é anônima mesmo.
import { Tracker } from "./tracker.js";
import { ligarCaptura } from "./captura.js";
import * as decisao from "./decisao.js";
import * as resolve from "./resolve.js";
import * as contract from "./contract.js";

/** Tudo o que o bundle expõe em `window.SYNQi3`. */
export const API = { Tracker, ...decisao, ...resolve, ...contract };

/**
 * A tag que carregou este arquivo. `currentScript` é o caminho certo e falha em um caso conhecido
 * (carregamento assíncrono/injetado), então há a busca por atributo como reserva.
 */
export function tagDoSdk(doc) {
  const d = doc || (typeof document !== "undefined" ? document : null);
  if (!d) return null;
  return (d.currentScript && d.currentScript.dataset && d.currentScript.dataset.synqi3Tenant
            ? d.currentScript
            : d.querySelector("script[data-synqi3-tenant]")) || null;
}

/**
 * A configuração declarada na tag. Só o que está escrito — nada de padrão inventado.
 *
 * Os nomes seguem o vocabulário que a v1 já usava (`data-synqi3-*`): um SDK com duas convenções de
 * configuração é um SDK que ninguém sabe configurar.
 */
export function configDaTag(tag) {
  const d = (tag && tag.dataset) || {};
  const cfg = {
    tenantId: d.synqi3Tenant || "",
    sdkToken: d.synqi3Token || "",
    baseUrl: d.synqi3Api || "",
    wsUrl: d.synqi3Ws || undefined,
    userId: d.synqi3User || undefined,
    // Slots explícitos são a saída para quem não quer marcar o HTML com `data-synqi3-slot`.
    slots: (d.synqi3Slots || "").split(",").map((s) => s.trim()).filter(Boolean),
    // Duas recusas explícitas, porque as duas custam: uma sai pela rede, a outra troca a tela.
    semPersonalizar: _flag(d.synqi3NoPersonalize),
    semAplicarAoRenovar: _flag(d.synqi3NoFlicker),
    // A CAPTURA é LIGADA por padrão, e isto é a mudança de 02/10/2026: até então a tag montava o
    // Tracker e não mandava evento nenhum, então uma página instalada media zero. As duas recusas
    // existem porque as duas custam, e são diferentes:
    //
    //   `data-synqi3-no-captura`      desliga TUDO, inclusive o `page_view`
    //   `data-synqi3-no-comportamento` mantém `page_view` e `session_start`, tira rolagem/clique/
    //                                  formulário/saída — para quem quer audiência e não comportamento
    semCaptura: _flag(d.synqi3NoCaptura),
    semComportamento: _flag(d.synqi3NoComportamento),
    // O Global Privacy Control é respeitado por padrão. Ver `captura.js`: o risco de ignorá-lo é do
    // DONO DA PÁGINA, então a decisão de dispensá-lo é dele e vai escrita na tag.
    ignorarGpc: _flag(d.synqi3IgnoreGpc),
  };
  if (d.synqi3Flush) {
    const ms = parseInt(d.synqi3Flush, 10);
    if (ms >= 0) cfg.flush = { windowMs: ms };
  }
  return cfg;
}

function _flag(v) {
  return v !== undefined && String(v).toLowerCase() !== "false" && v !== "0";
}

/**
 * Monta o Tracker e roda o ciclo. Devolve `null` quando a tag não declara o necessário.
 *
 * NUNCA levanta: um erro aqui roda dentro da página de um cliente, e derrubar o site dele por causa
 * de personalização seria o pior desfecho possível deste produto.
 */
export function iniciar(cfg, { Tracker: TrackerImpl = Tracker } = {}) {
  if (!cfg || !cfg.tenantId || !cfg.sdkToken) return null;
  let t;
  try {
    t = new TrackerImpl(cfg);
  } catch {
    return null;
  }
  if (!cfg.semCaptura) {
    // A MESMA MONTAGEM, GUARDADA. `consent(true)` precisa religar a captura, e só esta cola
    // sabe com que opções ela foi montada — reconstruí-las dentro do Tracker seria a segunda cópia
    // da configuração da tag, que divergiria na primeira opção nova.
    const montar = () => ligarCaptura(t, {
      ignorarGpc: cfg.ignorarGpc,
      comportamento: !cfg.semComportamento,
    });
    t._religarCaptura = montar;
    // NUNCA levanta: a captura inteira é best-effort, e derrubar a página de quem nos hospeda por
    // causa de um evento de rolagem seria o pior desfecho possível deste produto.
    try {
      t.captura = montar();
    } catch { /* a medição para; a página do cliente não */ }
  }
  if (!cfg.semPersonalizar) {
    try {
      // Não é aguardado de propósito: o pedaço que importa (aplicar o guardado) é SÍNCRONO e já
      // aconteceu quando esta linha retorna. O resto é a renovação, que roda em segundo plano.
      const p = t.personalizar({
        slots: cfg.slots,
        aplicarAoRenovar: !cfg.semAplicarAoRenovar,
      });
      if (p && p.catch) p.catch(() => {});
    } catch { /* personalização nunca derruba a página de quem nos hospeda */ }
  }
  return t;
}

/** Liga tudo a partir do documento. Chamado pelo bundle; exportado para poder ser testado. */
export function autoIniciar(doc, alvo) {
  const g = alvo || (typeof globalThis !== "undefined" ? globalThis : null);
  if (!g) return null;
  g.SYNQi3 = API;                                  // a superfície pública, sempre
  const cfg = configDaTag(tagDoSdk(doc));
  const t = iniciar(cfg);
  if (t) g.synqi3 = t;                             // a instância, só quando existe de verdade
  return t;
}
